import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, ChevronRight, CircleDot, ExternalLink, Plus, X,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtDateTime, zonedDateTimeToIso } from "../lib/time";

const BUCKETS = ["today", "overdue", "week", "completed"];
const PRIORITIES = ["low", "normal", "high", "critical"];

const statusColor = (status) => (
  status === "completed" ? "var(--pc-score-good)"
    : status === "in_progress" ? "var(--pc-cyan)" : "var(--pc-score-medium)"
);

function TaskDrawer({ task, resolution, setResolution, error, success, onClose, onStatus }) {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const resolutionRequired = task.source_type !== "manual" || task.priority === "critical";
  const canComplete = !resolutionRequired || Boolean(resolution.trim());
  const sourceLabel = task.source
    ? `${t(`taskSource_${task.source.type}`)} · ${task.source.title}`
    : task.task_origin === "routine" ? t("taskSource_routine") : t("manual");
  const sourceDeepLink = task.source?.source_context?.deep_link || task.source?.deep_link || task.deep_link;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return <div className="fixed inset-0 z-[70]" data-testid="care-task-drawer-layer">
    <button type="button" aria-label={t("close")} className="absolute inset-0 w-full h-full"
      style={{ background: "rgba(0,0,0,.48)", backdropFilter: "blur(2px)" }} onClick={onClose}
      data-testid="care-task-drawer-backdrop" />
    <aside className="absolute inset-y-0 right-0 w-full max-w-[560px] lg:w-[540px] flex flex-col shadow-2xl"
      style={{ background: "var(--pc-black)", borderLeft: "1px solid var(--pc-cyan-border)" }}
      role="dialog" aria-modal="true" aria-labelledby="care-task-drawer-title"
      data-testid="care-task-drawer">
      <header className="sticky top-0 z-10 px-6 py-5 flex items-start gap-4"
        style={{ background: "color-mix(in srgb, var(--pc-black) 94%, transparent)", borderBottom: "1px solid var(--pc-border)", backdropFilter: "blur(16px)" }}>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[.16em]" style={{ color: "var(--pc-cyan)" }}>{t("careTask")}</p>
          <h2 id="care-task-drawer-title" className="text-2xl font-display font-bold mt-1 leading-tight">{task.title}</h2>
          <span className="pc-chip inline-flex mt-3 uppercase font-semibold" style={{ color: statusColor(task.status), background: "var(--pc-card-inset)" }} data-testid="care-task-status-badge">
            {t(`careTaskStatus_${task.status}`)}
          </span>
        </div>
        <button type="button" className="pc-btn pc-btn-ghost shrink-0" onClick={onClose} aria-label={t("close")} data-testid="close-care-task-drawer"><X size={17} /></button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto pc-scroll px-6 py-5" data-testid="care-task-drawer-scroll">
        {error && <div className="rounded-xl p-3 mb-4 text-sm" role="alert" style={{ color: "var(--pc-score-poor)", background: "var(--pc-score-poor-soft)", border: "1px solid color-mix(in srgb, var(--pc-score-poor) 40%, transparent)" }}>{error}</div>}
        {success && <div className="rounded-xl p-3 mb-4 text-sm font-semibold" role="status" style={{ color: "var(--pc-score-good)", background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }}>{success}</div>}

        <section aria-label={t("taskDetails")} className="grid grid-cols-2 gap-3 text-sm">
          {[
            [t("player"), task.player?.name || "–"],
            [t("priority"), t(task.priority)],
            [t("due"), task.due_at ? fmtDateTime(task.due_at, task.player?.timezone, lang) : "–"],
            [t("assignedTo"), task.assigned_care_manager?.name || task.effective_care_manager?.name || "–"],
          ].map(([label, value]) => <div key={label} className="rounded-xl p-3" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }}>
            <p className="text-[10px] uppercase tracking-[.12em]" style={{ color: "var(--pc-muted)" }}>{label}</p>
            <p className="font-semibold mt-1">{value}</p>
          </div>)}
        </section>

        <section className="rounded-2xl p-4 mt-5" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-cyan-border)" }} data-testid="task-source-context">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[.13em]" style={{ color: "var(--pc-muted)" }}>{t("source")}</p>
              <p className="font-semibold mt-1">{sourceLabel}</p>
              {task.source?.source_context && <>
                <p className="text-xs mt-3" style={{ color: "var(--pc-cyan)" }}>{t("triggeredBy")}: {task.source.source_context.title}</p>
                {task.source.source_context.occurred_at && <p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{fmtDateTime(task.source.source_context.occurred_at, task.player?.timezone, lang)}</p>}
                {(task.source.source_context.original_context || []).map((line, index) => <p key={index} className="text-sm mt-2">{line}</p>)}
              </>}
            </div>
            {sourceDeepLink && <button type="button" className="pc-btn pc-btn-ghost flex items-center gap-2 shrink-0" onClick={() => nav(sourceDeepLink)}><ExternalLink size={15} />{t("openSource")}</button>}
          </div>
        </section>

        <section className="mt-5">
          <p className="text-[10px] uppercase tracking-[.13em]" style={{ color: "var(--pc-muted)" }}>{t("description")}</p>
          <p className="text-sm mt-2 leading-relaxed">{task.description || t("noDescription")}</p>
        </section>

        {task.status !== "completed" ? <section className="mt-6" data-testid="care-task-actions">
          <label className="block text-sm font-semibold" htmlFor="care-task-resolution">{t("resolution")}{resolutionRequired ? " *" : ""}</label>
          <p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{t("resolutionHint")}</p>
          <textarea id="care-task-resolution" className="pc-input mt-3" rows="5" value={resolution}
            onChange={(event) => setResolution(event.target.value)} placeholder={t("resolutionPlaceholder")} />
          <div className="flex flex-wrap gap-2 mt-4">
            {task.status === "open" && <button type="button" className="pc-btn pc-btn-ghost flex items-center gap-2" onClick={() => onStatus("in_progress")} data-testid="start-care-task"><CircleDot size={15} />{t("startWork")}</button>}
            <button type="button" className="pc-btn pc-btn-primary flex items-center gap-2" onClick={() => onStatus("completed")}
              disabled={!canComplete} data-testid="complete-care-task"><CheckCircle2 size={15} />{t("completeTask")}</button>
          </div>
          {resolutionRequired && !canComplete && <p className="text-xs mt-2" style={{ color: "var(--pc-score-medium)" }}>{t("resolutionRequired")}</p>}
        </section> : <section className="rounded-2xl p-4 mt-6" style={{ background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }} data-testid="completed-care-task-summary">
          <p className="font-semibold flex items-center gap-2" style={{ color: "var(--pc-score-good)" }}><CheckCircle2 size={17} />{t("careTaskStatus_completed")}</p>
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div><p className="text-[10px] uppercase tracking-[.12em]" style={{ color: "var(--pc-muted)" }}>{t("completedAt")}</p><p className="mt-1">{task.completed_at ? fmtDateTime(task.completed_at, task.player?.timezone, lang) : "–"}</p></div>
            <div><p className="text-[10px] uppercase tracking-[.12em]" style={{ color: "var(--pc-muted)" }}>{t("completedBy")}</p><p className="mt-1">{task.completed_by_user?.name || "–"}</p></div>
          </div>
          <p className="text-[10px] uppercase tracking-[.12em] mt-4" style={{ color: "var(--pc-muted)" }}>{t("resolution")}</p>
          <p className="text-sm mt-2 whitespace-pre-wrap">{task.resolution || "–"}</p>
        </section>}

        <section className="mt-6 pb-4">
          <h3 className="font-semibold">{t("history")}</h3>
          <div className="space-y-2 mt-3">{(task.history || []).slice().reverse().map((row, index) => <div key={`${row.at}-${index}`} className="text-xs border-l-2 pl-3 py-1" style={{ borderColor: "var(--pc-border)", color: "var(--pc-muted)" }}>{t(`taskHistory_${row.action}`)} · {fmtDateTime(row.at, task.player?.timezone, lang)}{row.resolution ? ` · ${row.resolution}` : ""}</div>)}</div>
        </section>
      </div>
    </aside>
  </div>;
}

export default function CareTasksWorkspace({ players = [], careScope = "", onChanged }) {
  const { t, lang } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const bucket = searchParams.get("bucket") || "today";
  const playerId = searchParams.get("player") || "";
  const priority = searchParams.get("priority") || "";
  const taskId = searchParams.get("task") || "";
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resolution, setResolution] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [draft, setDraft] = useState({ title: "", description: "", player_id: "", priority: "normal", due_date: "", due_time: "" });
  const playerRows = useMemo(() => players.map((row) => row.player || row), [players]);

  const load = useCallback(async () => {
    setError("");
    const params = { bucket };
    if (careScope) params.scope = careScope;
    if (playerId) params.player_id = playerId;
    if (priority) params.priority = priority;
    const { data } = await api.get("/care/tasks", { params });
    const rows = data.tasks || [];
    setTasks(rows);
    if (!taskId) {
      setSelected(null);
      setResolution("");
      return;
    }
    let task = rows.find((row) => row.id === taskId);
    if (!task) {
      const response = await api.get(`/care/tasks/${encodeURIComponent(taskId)}`);
      task = response.data.task;
    }
    setSelected(task);
    setResolution(task.resolution || "");
  }, [bucket, careScope, playerId, priority, taskId]);

  useEffect(() => {
    load().catch((exc) => setError(apiError(exc.response?.data?.detail) || t("todayError")));
  }, [load, t]);

  const closeTask = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("task");
    setSearchParams(next, { replace: true });
    setSelected(null);
    setResolution("");
    setError("");
    setSuccess("");
  }, [searchParams, setSearchParams]);

  const setFilter = (name, value) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "tasks");
    if (value) next.set(name, value); else next.delete(name);
    next.delete("task");
    setSearchParams(next);
    setSelected(null);
    setSuccess("");
  };

  const openTask = (task) => {
    setSelected(task);
    setResolution(task.resolution || "");
    setError("");
    setSuccess("");
    const next = new URLSearchParams(searchParams);
    next.set("view", "tasks");
    next.set("bucket", bucket);
    next.set("task", task.id);
    setSearchParams(next);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const player = playerRows.find((row) => row.id === draft.player_id);
    try {
      await api.post("/care/tasks", {
        title: draft.title, description: draft.description,
        player_id: draft.player_id || null, priority: draft.priority,
        due_at: draft.due_date && draft.due_time ? zonedDateTimeToIso(draft.due_date, draft.due_time, player?.timezone || "UTC") : null,
        source_type: "manual",
      });
      setDraft({ title: "", description: "", player_id: "", priority: "normal", due_date: "", due_time: "" });
      setCreating(false);
      await load();
      onChanged?.();
    } catch (exc) {
      const detail = apiError(exc.response?.data?.detail);
      setError(/resolution is required/i.test(detail) ? t("resolutionRequired") : detail);
    }
  };

  const setStatus = async (status) => {
    setError("");
    setSuccess("");
    try {
      const payload = { status, resolution: status === "completed" ? (resolution.trim() || null) : null };
      const { data } = await api.post(`/care/tasks/${selected.id}/status`, payload);
      setSelected(data.task);
      setResolution(data.task.resolution || "");
      if (status === "completed") setSuccess(t("taskCompletedSuccess"));
      await load();
      onChanged?.();
    } catch (exc) {
      const detail = apiError(exc.response?.data?.detail);
      setError(/resolution is required/i.test(detail) ? t("resolutionRequired") : detail);
    }
  };

  return <section data-testid="care-tasks-workspace">
    <div className="flex flex-wrap items-center gap-3 mb-5">
      <div className="flex flex-wrap gap-2">{BUCKETS.map((value) => <button key={value} type="button" onClick={() => setFilter("bucket", value)} className="pc-btn" data-testid={`task-bucket-${value}`} style={{ background: bucket === value ? "var(--pc-cyan)" : "var(--pc-charcoal)", color: bucket === value ? "#001014" : "var(--pc-muted)", padding: "8px 12px" }}>{t(`taskBucket_${value}`)}</button>)}</div>
      <select className="pc-input ml-auto max-w-[210px]" value={playerId} onChange={(event) => setFilter("player", event.target.value)} aria-label={t("player")}><option value="">{t("allPlayers")}</option>{playerRows.map((player) => <option key={player.id} value={player.id}>{player.name}</option>)}</select>
      <select className="pc-input max-w-[160px]" value={priority} onChange={(event) => setFilter("priority", event.target.value)} aria-label={t("priority")}><option value="">{t("allPriorities")}</option>{PRIORITIES.map((value) => <option key={value} value={value}>{t(value)}</option>)}</select>
      <button type="button" className="pc-btn pc-btn-primary flex items-center gap-2" onClick={() => setCreating(true)} data-testid="create-care-task"><Plus size={15} />{t("newCareTask")}</button>
    </div>
    {error && !selected && <div className="pc-card p-3 mb-4 text-sm" role="alert" style={{ color: "var(--pc-score-poor)" }}>{error}</div>}
    <div className="pc-ops-panel overflow-hidden">
      <div className="px-4 py-2.5 grid grid-cols-[minmax(0,1.7fr)_130px_minmax(110px,1fr)_150px_90px_125px] gap-3 text-[10px] uppercase tracking-[.12em]" style={{ color: "var(--pc-muted)", background: "var(--pc-card-inset)" }}><span>{t("task")}</span><span>{t("player")}</span><span>{t("source")}</span><span>{t("due")}</span><span>{t("priority")}</span><span>{t("status")}</span></div>
      {tasks.length === 0 ? <p className="p-8 text-center text-sm" style={{ color: "var(--pc-muted)" }}>{t("noCareTasks")}</p> : <div className="divide-y" style={{ borderColor: "var(--pc-border)" }}>{tasks.map((task) => <button type="button" key={task.id} onClick={() => openTask(task)} data-testid={`care-task-${task.id}`} className="pc-care-task-row w-full px-4 py-3 grid grid-cols-[minmax(0,1.7fr)_130px_minmax(110px,1fr)_150px_90px_125px] gap-3 items-center text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--pc-cyan)]" style={{ color: "var(--pc-text)", background: selected?.id === task.id ? "var(--pc-cyan-dim)" : undefined, borderColor: "var(--pc-border)" }}><span className="font-semibold truncate">{task.title}</span><span className="text-xs truncate" style={{ color: "var(--pc-muted)" }}>{task.player?.name || "–"}</span><span className="text-xs truncate" style={{ color: "var(--pc-cyan)" }}>{task.source ? `${t(`taskSource_${task.source.type}`)} · ${task.source.title}` : task.task_origin === "routine" ? t("taskSource_routine") : t("manual")}</span><span className="text-xs">{task.due_at ? fmtDateTime(task.due_at, task.player?.timezone, lang) : "–"}</span><span className="text-[10px] uppercase font-semibold" style={{ color: task.priority === "critical" ? "var(--pc-score-poor)" : task.priority === "high" ? "var(--pc-score-medium)" : "var(--pc-muted)" }}>{t(task.priority)}</span><span className="text-xs flex items-center justify-between gap-2">{t(`careTaskStatus_${task.status}`)}<ChevronRight size={14} style={{ color: "var(--pc-cyan)" }} /></span></button>)}</div>}
    </div>

    {selected && <TaskDrawer task={selected} resolution={resolution} setResolution={setResolution}
      error={error} success={success} onClose={closeTask} onStatus={setStatus} />}

    {creating && <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,.76)" }}><form onSubmit={submit} className="pc-card p-5 w-full max-w-xl" data-testid="care-task-form"><div className="flex items-center"><h2 className="text-xl font-display font-bold">{t("newCareTask")}</h2><button type="button" className="pc-btn pc-btn-ghost ml-auto" onClick={() => setCreating(false)}><X size={16} /></button></div><label className="block text-sm font-semibold mt-5">{t("title")}</label><input required aria-label={t("title")} className="pc-input mt-2" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /><label className="block text-sm font-semibold mt-5">{t("internalDescription")}</label><textarea aria-label={t("internalDescription")} className="pc-input mt-2" rows="3" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /><div className="grid sm:grid-cols-2 gap-3 mt-4"><select aria-label={t("player")} className="pc-input" value={draft.player_id} onChange={(event) => setDraft({ ...draft, player_id: event.target.value })}><option value="">{t("noPlayerReference")}</option>{playerRows.map((player) => <option key={player.id} value={player.id}>{player.name}</option>)}</select><select aria-label={t("priority")} className="pc-input" value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}>{PRIORITIES.map((value) => <option key={value} value={value}>{t(value)}</option>)}</select><input aria-label={t("dueDate")} type="date" className="pc-input" value={draft.due_date} onChange={(event) => setDraft({ ...draft, due_date: event.target.value })} /><input aria-label={t("dueTime")} type="time" className="pc-input" value={draft.due_time} onChange={(event) => setDraft({ ...draft, due_time: event.target.value })} /></div><button className="pc-btn pc-btn-primary mt-5 w-full" type="submit">{t("create")}</button></form></div>}
  </section>;
}
